
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Member } from '@/types/member';
import { toast } from '@/components/ui/use-toast';

interface ConnectionRequestDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectedMember: Member | null;
  onSendRequest: (targetUserId: string, message: string) => Promise<boolean>;
}

const ConnectionRequestDialog: React.FC<ConnectionRequestDialogProps> = ({
  isOpen,
  setIsOpen,
  selectedMember,
  onSendRequest
}) => {
  const [connectionMessage, setConnectionMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedMember || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const target_user_id = selectedMember.user_id || selectedMember.id;
      const success = await onSendRequest(target_user_id, connectionMessage);
      
      if (success) {
        setConnectionMessage('');
        setIsOpen(false);
        toast({
          title: "Demande envoyée",
          description: `Votre demande de connexion a été envoyée à ${selectedMember.name}`,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Demander une mise en relation</DialogTitle>
          <DialogDescription>
            {selectedMember && (
              <span>
                Envoyez une demande pour entrer en contact avec {selectedMember.name}.
                Précisez l'objet de votre demande pour faciliter la mise en relation.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="flex items-center space-x-4">
            {selectedMember && (
              <>
                <div className="h-12 w-12 rounded-full overflow-hidden">
                  <img src={selectedMember.image} alt={selectedMember.name} className="h-full w-full object-cover" />
                </div>
                <div>
                  <h4 className="font-bold">{selectedMember.name}</h4>
                  <p className="text-sm text-gray-500">{selectedMember.role} — {selectedMember.company}</p>
                </div>
              </>
            )}
          </div>
          
          <div>
            <label htmlFor="message" className="block text-sm font-medium mb-1">
              Message personnalisé
            </label>
            <textarea
              id="message"
              rows={5}
              value={connectionMessage}
              onChange={(e) => setConnectionMessage(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-node-copper focus:ring-node-copper"
              placeholder="Expliquez brièvement pourquoi vous souhaitez entrer en contact..."
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            className="bg-node-copper hover:bg-node-copper/90 text-white disabled:opacity-50"
          >
            {isSubmitting ? 'Envoi...' : 'Envoyer la demande'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConnectionRequestDialog;
